import { EMOTES, EMOTE_MAP } from '../config/emotes';
import { retargetClipToRig } from '../avatar/emoteRetarget';
import { AvatarSocialMotion } from '../avatar/social';
import AvatarFacePanel, { emptyFacePreview, FacePreviewSettings } from '../components/AvatarFacePanel';
import { prepareLocomotion, hasAnimatedPose } from '../avatar/locomotion';
import { AvatarIdleMotion } from '../avatar/animation';
import { useAvatarAnimator } from '../avatar/useAvatarAnimator';
import React, { useState, useEffect, useMemo, useRef, useCallback, Suspense } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Grid, useGLTF } from '@react-three/drei';
import { clone } from 'three/examples/jsm/utils/SkeletonUtils';
import * as THREE from 'three';
import './AvatarSandbox.css';
import { inspectAvatarRig, AvatarManifest } from '../avatar/rig';
import AvatarRigInspector from '../components/AvatarRigInspector';

function SandboxFraming({ face, height }: { face: boolean; height: number }) {
  const { camera } = useThree();
  useEffect(() => { camera.position.set(0, face ? height : 1, face ? .55 : 3); camera.lookAt(0, face ? height : .8, 0); }, [camera,face,height]);
  return null;
}

// ---- Types ----
interface BoneState {
  name: string;
  bone: THREE.Bone;
  rx: number; ry: number; rz: number;
  restQ: THREE.Quaternion;
}

// ---- 3D Avatar with animation + bone controls ----
const SandboxAvatar: React.FC<{
  url: string;
  onBonesReady: (bones: BoneState[]) => void;
  onRigReady: (manifest: AvatarManifest) => void;
  boneUpdates: Record<string, { rx: number; ry: number; rz: number }>;
  showSkeleton: boolean;
  breathing: boolean;
  facePreview: FacePreviewSettings;
  activeAnim: string; // 'none' | 'idle' | 'walk' | 'run'
  onAnimFrame: (boneData: Record<string, { rx: number; ry: number; rz: number }>) => void;
}> = ({ url, onBonesReady, onRigReady, boneUpdates, showSkeleton, breathing, facePreview, activeAnim, onAnimFrame }) => {
  const gltf = useGLTF(url);
  // Inspect the original asset before the sandbox renames or animates its clone.
  const rig = useMemo(() => inspectAvatarRig(gltf.scene, url), [gltf.scene, url]);
  useEffect(() => { onRigReady(rig.manifest); }, [rig, onRigReady]);

  // Load Meshy-generated animations if available, otherwise Mixamo fallbacks
  const meshyAnimUrls = useMemo(() => {
    try {
      if (localStorage.getItem('eb-avatar-url') !== url) return null;
      const stored = localStorage.getItem('eb-avatar-animations');
      return stored ? JSON.parse(stored) as Record<string, string> : null;
    } catch { return null; }
  }, [url]);

  const idleModel = useGLTF(meshyAnimUrls?.idle || '/animations/idle-male.glb');
  const walkModel = useGLTF(meshyAnimUrls?.walking || '/animations/walk-male.glb');
  const runModel = useGLTF(meshyAnimUrls?.running || '/animations/run-male.glb');
  const emoteModel = useGLTF(EMOTE_MAP[activeAnim]?.url || '/animations/idle-male.glb');
  const hasMeshyAnims = !!(meshyAnimUrls?.walking || meshyAnimUrls?.running);

  const bonesRef = useRef<BoneState[]>([]);
  const prevAnim = useRef('none');
  const frameCount = useRef(0);

  const cloned = useMemo(() => {
    const c = clone(gltf.scene);
    return c;
  }, [gltf.scene, hasMeshyAnims]);

  // Collect bones
  useEffect(() => {
    const bones: BoneState[] = [];
    cloned.traverse((child: any) => {
      if (child.isBone) {
        const euler = new THREE.Euler().setFromQuaternion(child.quaternion, 'XYZ');
        bones.push({
          name: child.name,
          bone: child,
          rx: THREE.MathUtils.radToDeg(euler.x),
          ry: THREE.MathUtils.radToDeg(euler.y),
          rz: THREE.MathUtils.radToDeg(euler.z),
          restQ: child.quaternion.clone(),
        });
      }
    });
    bonesRef.current = bones;
    onBonesReady(bones);
  }, [cloned, onBonesReady]);

  // Build animations — Meshy animations are natively compatible
  const animations = useMemo(() => {
    const remap = (clip: THREE.AnimationClip, name: string, source?: THREE.Object3D) => prepareLocomotion(cloned, clip, name, source);

    const anims: THREE.AnimationClip[] = [];

    // A short embedded reference pose is not a standing idle.
    const embeddedIdle = gltf.animations.find(hasAnimatedPose);
    if (meshyAnimUrls?.idle && idleModel.animations[0]) {
      anims.push(remap(idleModel.animations[0], 'idle'));
    } else if (embeddedIdle) {
      anims.push(remap(embeddedIdle, 'idle'));
      console.log(`[Sandbox] Using built-in idle animation`);
    } else if (idleModel.animations.length > 0) {
      anims.push(remap(idleModel.animations[0], 'idle', idleModel.scene));
    }

    if (walkModel.animations.length > 0) {
      anims.push(remap(walkModel.animations[0], 'walk', meshyAnimUrls?.walking ? undefined : walkModel.scene));
      console.log(`[Sandbox] Walk source: ${hasMeshyAnims ? 'Meshy' : 'Mixamo'}`);
    }
    if (runModel.animations.length > 0) {
      anims.push(remap(runModel.animations[0], 'run', meshyAnimUrls?.running ? undefined : runModel.scene));
      console.log(`[Sandbox] Run source: ${hasMeshyAnims ? 'Meshy' : 'Mixamo'}`);
    }
    if (EMOTE_MAP[activeAnim] && emoteModel.animations[0]) {
      const rests = new Map<string, THREE.Quaternion>();
      const order: { name: string; parent: string }[] = [];
      gltf.scene.updateMatrixWorld(true);
      gltf.scene.traverse((node: any) => {
        rests.set(node.name, node.getWorldQuaternion(new THREE.Quaternion()));
        if (node.isBone) order.push({ name: node.name, parent: node.parent?.name || '' });
      });
      const clip = emoteModel.animations[0].clone(); clip.name = activeAnim;
      anims.push(retargetClipToRig(emoteModel.scene, clip, rests, order));
    }
    return anims;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloned, gltf.scene, gltf.animations, idleModel, walkModel, runModel, meshyAnimUrls, emoteModel, activeAnim]);

  const idleMotion = useMemo(() => {
    const mapped = inspectAvatarRig(cloned);
    return new AvatarIdleMotion(mapped.manifest.capabilities.bodyMapped ? mapped.bones.get('chest') || mapped.bones.get('spine') : undefined, url);
  }, [cloned, url]);
  useEffect(() => () => idleMotion.clear(), [idleMotion]);
  const facialMotion = useMemo(() => new AvatarSocialMotion(inspectAvatarRig(cloned), cloned, url), [cloned,url]);
  useEffect(() => () => facialMotion.clear(), [facialMotion]);
  const speechTime = useRef(0);
  const animator = useAvatarAnimator(animations, cloned);
  const { actions, mixer } = animator;

  useEffect(() => { prevAnim.current = "none"; }, [animator]);

  // Handle animation switching
  useEffect(() => {
    if (activeAnim === prevAnim.current) return;
    const previous = prevAnim.current;
    if (activeAnim === 'none') animator.stop();
    else animator.transition(previous, activeAnim, .65);
    prevAnim.current = activeAnim;
  }, [activeAnim, animator]);


  useFrame((_, delta) => {
    facialMotion.clear();
    idleMotion.clear();
    if (activeAnim !== 'none' && mixer) {
      // Let mixer set bone quaternions
      mixer.update(delta);

      idleMotion.apply(delta, breathing && activeAnim === 'idle');

      // Report bone positions back to UI every 10 frames
      frameCount.current++;
      if (frameCount.current % 10 === 0) {
        const data: Record<string, { rx: number; ry: number; rz: number }> = {};
        bonesRef.current.forEach(bs => {
          const e = new THREE.Euler().setFromQuaternion(bs.bone.quaternion, 'XYZ');
          data[bs.name] = {
            rx: Math.round(THREE.MathUtils.radToDeg(e.x) * 10) / 10,
            ry: Math.round(THREE.MathUtils.radToDeg(e.y) * 10) / 10,
            rz: Math.round(THREE.MathUtils.radToDeg(e.z) * 10) / 10,
          };
        });
        onAnimFrame(data);
      }
    } else {
      // No animation — apply manual bone slider values
      for (const bs of bonesRef.current) {
        const update = boneUpdates[bs.name];
        if (update) {
          bs.bone.rotation.set(
            THREE.MathUtils.degToRad(update.rx),
            THREE.MathUtils.degToRad(update.ry),
            THREE.MathUtils.degToRad(update.rz)
          );
        }
      }
    }
    speechTime.current += delta;
    const voice = facePreview.speech ? Math.max(0,Math.sin(speechTime.current*9))* .28 : 0;
    facialMotion.apply(cloned,delta,voice,undefined,facePreview);
  });

  const helper = useMemo(() => {
    const h = new THREE.SkeletonHelper(cloned);
    (h.material as THREE.LineBasicMaterial).linewidth = 2;
    return h;
  }, [cloned, gltf.scene, gltf.animations, idleModel, walkModel, runModel, meshyAnimUrls, emoteModel, activeAnim]);

  return (
    <>
      <primitive object={cloned} scale={[1, 1, 1]} />
      {showSkeleton && <primitive object={helper} />}
    </>
  );
};

// ---- Bone Slider Control ----
const BoneControl: React.FC<{
  name: string;
  rx: number; ry: number; rz: number;
  restRx: number; restRy: number; restRz: number;
  onChange: (name: string, axis: 'rx' | 'ry' | 'rz', value: number) => void;
  onReset: (name: string) => void;
  isSelected: boolean;
  onSelect: (name: string) => void;
  disabled: boolean;
}> = ({ name, rx, ry, rz, restRx, restRy, restRz, onChange, onReset, isSelected, onSelect, disabled }) => {
  const hasChanged = Math.abs(rx - restRx) > 0.5 || Math.abs(ry - restRy) > 0.5 || Math.abs(rz - restRz) > 0.5;

  return (
    <div className={`bone-control ${isSelected ? 'selected' : ''} ${hasChanged ? 'changed' : ''}`} onClick={() => onSelect(name)}>
      <div className="bone-header">
        <span className="bone-name">{name}</span>
        <div className="bone-header-right">
          <span className="bone-values-mini">
            {Math.round(rx)}° {Math.round(ry)}° {Math.round(rz)}°
          </span>
          {hasChanged && !disabled && <button className="bone-reset" onClick={(e) => { e.stopPropagation(); onReset(name); }}>Reset</button>}
        </div>
      </div>
      {isSelected && (
        <div className="bone-sliders">
          {(['rx', 'ry', 'rz'] as const).map((axis, i) => {
            const val = axis === 'rx' ? rx : axis === 'ry' ? ry : rz;
            const label = ['X', 'Y', 'Z'][i];
            const color = ['#ff4444', '#44ff44', '#4488ff'][i];
            return (
              <div key={axis} className="slider-row">
                <span className="axis-label" style={{ color }}>{label}</span>
                <input
                  type="range"
                  min={-180}
                  max={180}
                  step={0.5}
                  value={val}
                  onChange={(e) => onChange(name, axis, parseFloat(e.target.value))}
                  className="bone-slider"
                  disabled={disabled}
                />
                <input
                  type="number"
                  value={Math.round(val * 10) / 10}
                  onChange={(e) => onChange(name, axis, parseFloat(e.target.value) || 0)}
                  className="bone-number"
                  disabled={disabled}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---- Main Sandbox Page ----
const AvatarSandbox: React.FC = () => {
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [manifest, setManifest] = useState<AvatarManifest | null>(null);
  const [inspectorTab, setInspectorTab] = useState<'capabilities' | 'bones' | 'face'>('capabilities');
  const [bones, setBones] = useState<BoneState[]>([]);
  const [boneValues, setBoneValues] = useState<Record<string, { rx: number; ry: number; rz: number }>>({});
  const [restValues, setRestValues] = useState<Record<string, { rx: number; ry: number; rz: number }>>({});
  const [selectedBone, setSelectedBone] = useState<string>('');
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [breathing, setBreathing] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');
  const [facePreview,setFacePreview] = useState<FacePreviewSettings>(emptyFacePreview);
  const [activeAnim, setActiveAnim] = useState<string>('none');

  useEffect(() => {
    const requested = new URLSearchParams(window.location.search).get('avatar');
    if (requested) {
      try { const url = new URL(requested); if (url.protocol === 'https:' || url.protocol === 'http:') { setAvatarUrl(url.href); return; } } catch { /* Fall back to the saved avatar. */ }
    }
    const saved = localStorage.getItem('eb-avatar-url');
    if (saved && saved !== 'skip') setAvatarUrl(saved);
  }, []);

  const handleBonesReady = useCallback((newBones: BoneState[]) => {
    setBones(newBones);
    const vals: Record<string, { rx: number; ry: number; rz: number }> = {};
    const rest: Record<string, { rx: number; ry: number; rz: number }> = {};
    for (const b of newBones) {
      vals[b.name] = { rx: b.rx, ry: b.ry, rz: b.rz };
      rest[b.name] = { rx: b.rx, ry: b.ry, rz: b.rz };
    }
    setBoneValues(vals);
    setRestValues(rest);
  }, []);

  // When animation is playing, update the UI bone values to reflect what's happening
  const handleAnimFrame = useCallback((data: Record<string, { rx: number; ry: number; rz: number }>) => {
    setBoneValues(data);
  }, []);

  const handleChange = useCallback((name: string, axis: 'rx' | 'ry' | 'rz', value: number) => {
    setBoneValues(prev => ({ ...prev, [name]: { ...prev[name], [axis]: value } }));
  }, []);

  const handleReset = useCallback((name: string) => {
    setBoneValues(prev => ({ ...prev, [name]: { ...restValues[name] } }));
  }, [restValues]);

  const handleResetAll = useCallback(() => {
    setBoneValues({ ...restValues });
    setActiveAnim('none');
  }, [restValues]);

  const handleExport = useCallback(() => {
    const corrections: Record<string, { rx: number; ry: number; rz: number }> = {};
    for (const [name, val] of Object.entries(boneValues)) {
      const rest = restValues[name];
      if (!rest) continue;
      const dx = val.rx - rest.rx, dy = val.ry - rest.ry, dz = val.rz - rest.rz;
      if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5 || Math.abs(dz) > 0.5) {
        corrections[name] = { rx: Math.round(dx * 10) / 10, ry: Math.round(dy * 10) / 10, rz: Math.round(dz * 10) / 10 };
      }
    }
    const json = JSON.stringify(corrections, null, 2);
    console.log('[Sandbox] Bone corrections (delta from rest):', json);
    navigator.clipboard.writeText(json);
    alert('Corrections copied to clipboard!');
  }, [boneValues, restValues]);

  const handleExportAbsolute = useCallback(() => {
    const pose: Record<string, { rx: number; ry: number; rz: number }> = {};
    for (const [name, val] of Object.entries(boneValues)) {
      pose[name] = { rx: Math.round(val.rx * 10) / 10, ry: Math.round(val.ry * 10) / 10, rz: Math.round(val.rz * 10) / 10 };
    }
    const json = JSON.stringify(pose, null, 2);
    console.log('[Sandbox] Absolute bone pose:', json);
    navigator.clipboard.writeText(json);
    alert('Absolute pose copied to clipboard!');
  }, [boneValues]);

  const filteredBones = bones.filter(b => b.name.toLowerCase().includes(searchFilter.toLowerCase()));
  const isAnimating = activeAnim !== 'none';

  const changedCount = Object.entries(boneValues).filter(([name, val]) => {
    const rest = restValues[name];
    if (!rest) return false;
    return Math.abs(val.rx - rest.rx) > 0.5 || Math.abs(val.ry - rest.ry) > 0.5 || Math.abs(val.rz - rest.rz) > 0.5;
  }).length;

  if (!avatarUrl) {
    return (
      <div className="sandbox-page">
        <div className="sandbox-empty">
          <h2>Avatar Sandbox</h2>
          <p>No avatar found. Create one first from your Profile page.</p>
          <button onClick={() => window.location.href = '/profile'}>Go to Profile</button>
        </div>
      </div>
    );
  }

  return (
    <div className="sandbox-page">
      <div className="sandbox-viewport">
        <Canvas camera={{ position: [0, 1, 3], fov: 50 }}>
          <SandboxFraming face={inspectorTab === 'face'} height={(manifest?.bones.head?.modelPosition[1] || 1.49) + .09} />
          <ambientLight intensity={0.6} />
          <directionalLight position={[3, 5, 3]} intensity={0.8} />
          <directionalLight position={[-3, 2, -3]} intensity={0.3} />
          <Suspense fallback={null}>
            <SandboxAvatar
              url={avatarUrl}
              onBonesReady={handleBonesReady}
              onRigReady={setManifest}
              boneUpdates={boneValues}
              showSkeleton={showSkeleton}
              breathing={breathing}
              facePreview={inspectorTab === 'face' ? facePreview : emptyFacePreview}
              activeAnim={activeAnim}
              onAnimFrame={handleAnimFrame}
            />
          </Suspense>
          <Grid
            args={[10, 10]}
            position={[0, 0, 0]}
            cellSize={0.5}
            cellThickness={0.5}
            cellColor="#333"
            sectionSize={2}
            sectionThickness={1}
            sectionColor="#555"
            fadeDistance={10}
            infiniteGrid
          />
          <OrbitControls target={[0, inspectorTab === 'face' ? (manifest?.bones.head?.modelPosition[1] || 1.49) + .09 : .8, 0]} />
        </Canvas>

        <div className="viewport-overlay">
          <div className="viewport-info">
            <span>{bones.length} bones</span>
            {isAnimating && <span className="anim-badge">{activeAnim}</span>}
            {changedCount > 0 && <span className="changed-badge">{changedCount} modified</span>}
          </div>
        </div>
      </div>

      <div className="sandbox-panel">
        <div className="panel-header">
          <h2>Avatar Inspector</h2>
          <label className="skeleton-toggle">
            <input type="checkbox" checked={showSkeleton} onChange={(e) => setShowSkeleton(e.target.checked)} />
            Skeleton
          </label>
        </div>

        <div className="sandbox-inspector-tabs" aria-label="Inspector views">
          <button aria-pressed={inspectorTab === 'capabilities'} onClick={() => setInspectorTab('capabilities')}>Capabilities</button>
          <button aria-pressed={inspectorTab === 'bones'} onClick={() => setInspectorTab('bones')}>Bone editor</button>
          <button aria-pressed={inspectorTab === 'face'} onClick={() => setInspectorTab('face')}>Face</button>
        </div>
        {inspectorTab === 'face' ? <div className="sandbox-inspector-scroll"><AvatarFacePanel manifest={manifest} value={facePreview} onChange={setFacePreview}/></div> : inspectorTab === 'capabilities' ? <div className="sandbox-inspector-scroll"><AvatarRigInspector manifest={manifest} /></div> : <>
        {/* Animation controls */}
        <div className="anim-controls">
          <span className="anim-label">Animation:</span>
          {['none', 'idle', 'walk', 'run'].map(name => (
            <button
              key={name}
              className={`anim-btn ${activeAnim === name ? 'active' : ''}`}
              onClick={() => setActiveAnim(name)}
            >
              {name === 'none' ? 'Off' : name.charAt(0).toUpperCase() + name.slice(1)}
            </button>
          ))}
        </div>

        <label>Saved motion <select value={EMOTE_MAP[activeAnim] ? activeAnim : ''} onChange={e => setActiveAnim(e.target.value || 'none')}><option value="">Choose a motion</option>{EMOTES.map(e => <option key={e.name} value={e.name}>{e.label}</option>)}</select></label>
              <label className="sandbox-motion-toggle"><input type="checkbox" checked={breathing} onChange={e => setBreathing(e.target.checked)} /> Subtle breathing during idle</label>
        <div className="panel-toolbar">
          <input
            type="text"
            placeholder="Search bones..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="bone-search"
          />
          <button className="toolbar-btn" onClick={handleResetAll}>Reset All</button>
        </div>

        <div className="bone-list">
          {filteredBones.map(b => (
            <BoneControl
              key={b.name}
              name={b.name}
              rx={boneValues[b.name]?.rx ?? b.rx}
              ry={boneValues[b.name]?.ry ?? b.ry}
              rz={boneValues[b.name]?.rz ?? b.rz}
              restRx={restValues[b.name]?.rx ?? b.rx}
              restRy={restValues[b.name]?.ry ?? b.ry}
              restRz={restValues[b.name]?.rz ?? b.rz}
              onChange={handleChange}
              onReset={handleReset}
              isSelected={selectedBone === b.name}
              onSelect={setSelectedBone}
              disabled={isAnimating}
            />
          ))}
        </div>

        <div className="panel-footer">
          <button className="export-btn" onClick={handleExport}>
            Export Corrections (Delta)
          </button>
          <button className="export-btn secondary" onClick={handleExportAbsolute}>
            Export Absolute Pose
          </button>
        </div>
        </>}
      </div>
    </div>
  );
};

export default AvatarSandbox;
